import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/api';
import { ArrowLeft, Loader2, ChevronRight, RefreshCw, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

export default function InvoiceBuilder() {
  const { supplierId } = useParams();
  const navigate = useNavigate();
  const [si, setSi] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showInternal, setShowInternal] = useState(true);

  // Pricing inputs
  const [selectedTierId, setSelectedTierId] = useState('');
  const [markupPct, setMarkupPct] = useState(10);
  const [serviceFee, setServiceFee] = useState(75);
  const [taxRate, setTaxRate] = useState(0);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isCustomMarkup, setIsCustomMarkup] = useState(false);
  const [customerTier, setCustomerTier] = useState(null);

  const addManualItem = () => {
    const newItem = {
      id: `manual-${Date.now()}`,
      desc: '',
      desc_display: '',
      qty: 1,
      unit_cost: 0,
      line_cost: 0,
      selected: true,
      override_markup_percent: null,
      override_final_unit_price: null,
      is_manual: true,
    };
    setSelectedItems(prev => [...prev, newItem]);
  };

  const MARKUP_PRESETS = [
    { label: '2.5%', value: 2.5 },
    { label: '5%', value: 5 },
    { label: '7%', value: 7 },
  ];

  const loadData = async () => {
    setLoading(true);
    try {
      const [siRes, tiersRes] = await Promise.all([
        api.get(`/invoice-transformer/supplier-invoices/${supplierId}`),
        api.get('/invoice-transformer/pricing-tiers'),
      ]);
      setSi(siRes.data);
      setTiers(tiersRes.data);
      // Pre-select items
      const items = (siRes.data.extracted_items || []).map(item => ({
        ...item, selected: true, desc_display: item.desc, override_markup_percent: null, override_final_unit_price: null
      }));
      setSelectedItems(items);

      // Load customer's assigned pricing tier and auto-apply
      try {
        const tierRes = await api.get(`/pricing-tiers/customer/${siRes.data.customer_id}/tier`);
        const custTier = tierRes.data.tier;
        if (custTier) {
          setCustomerTier(custTier);
          setMarkupPct(custTier.markup_percent);
          setIsCustomMarkup(false);
        }
      } catch {
        // Fall back to default tier from invoice transformer tiers
        const defaultTier = tiersRes.data.find(t => t.is_default) || tiersRes.data[0];
        if (defaultTier) {
          setSelectedTierId(defaultTier.id);
          setMarkupPct(defaultTier.markup_percent);
          setServiceFee(defaultTier.service_fee);
          setTaxRate(defaultTier.tax_rate || 0);
          setIsCustomMarkup(false);
        }
      }
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, [supplierId]);

  const applyTier = (tierId) => {
    setSelectedTierId(tierId);
    setIsCustomMarkup(false);
    const tier = tiers.find(t => t.id === tierId);
    if (tier) {
      setMarkupPct(tier.markup_percent);
      setServiceFee(tier.service_fee);
      setTaxRate(tier.tax_rate || 0);
    }
  };

  // Live computation (per-item markup aware)
  const activeItems = selectedItems.filter(i => i.selected);
  const baseSubtotal = activeItems.reduce((a, i) => a + (parseFloat(i.qty) || 1) * (parseFloat(i.unit_cost) || 0), 0);
  const customerSubtotal = activeItems.reduce((a, item) => {
    const baseUnit = parseFloat(item.unit_cost) || 0;
    const qty = parseFloat(item.qty) || 1;
    const finalUnit = item.override_final_unit_price != null
      ? item.override_final_unit_price
      : item.override_markup_percent != null
      ? Math.round(baseUnit * (1 + item.override_markup_percent / 100) * 100) / 100
      : Math.round(baseUnit * (1 + markupPct / 100) * 100) / 100;
    return a + finalUnit * qty;
  }, 0);
  const markupAmt = Math.round((customerSubtotal - baseSubtotal) * 100) / 100;
  const subtotalAfterMarkup = Math.round(customerSubtotal * 100) / 100;
  const taxAmt = Math.round(subtotalAfterMarkup * (taxRate / 100) * 100) / 100;
  const totalDue = Math.round((subtotalAfterMarkup + parseFloat(serviceFee) + taxAmt) * 100) / 100;
  const profitEstimate = Math.round((markupAmt + parseFloat(serviceFee)) * 100) / 100;

  // Customer-facing items (with markup applied)
  const customerItems = activeItems.map(item => {
    const baseUnit = parseFloat(item.unit_cost) || 0;
    const qty = parseFloat(item.qty) || 1;
    const finalUnit = item.override_final_unit_price != null
      ? item.override_final_unit_price
      : item.override_markup_percent != null
      ? Math.round(baseUnit * (1 + item.override_markup_percent / 100) * 100) / 100
      : Math.round(baseUnit * (1 + markupPct / 100) * 100) / 100;
    return { desc: item.desc_display || item.desc, qty, unit_price: finalUnit, line_total: Math.round(finalUnit * qty * 100) / 100 };
  });

  const handleContinue = async () => {
    if (activeItems.length === 0) return toast.error('Select at least 1 line item');
    setCreating(true);
    try {
      const res = await api.post('/invoice-transformer/transform-runs', {
        supplier_invoice_id: supplierId,
        pricing_tier_id: selectedTierId || undefined,
        markup_percent: markupPct,
        service_fee: parseFloat(serviceFee),
        tax_rate: parseFloat(taxRate),
        selected_items: selectedItems,
      });
      navigate(`/ops/invoice-transformer/finalize/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create transform run');
    } finally {
      setCreating(false);
    }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="text-[#3B82F6] animate-spin" size={28} /></div>;

  return (
    <div className="p-6 space-y-5 animate-fade-in">
      <div className="flex items-center gap-4 pb-4 border-b border-[#2D3F5E]/60">
        <button onClick={() => navigate(`/ops/invoice-transformer/supplier/${supplierId}`)} className="text-[#5C6A7A] hover:text-[#3B82F6] transition-colors">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#5C6A7A] mb-0.5">// INVOICE BUILDER</p>
          <h1 className="text-white font-['Manrope'] font-extrabold text-xl">{si?.supplier_vendor_name || 'Invoice Builder'}</h1>
          <p className="text-[#B8C0CC] font-['IBM_Plex_Sans'] text-sm">
            {si?.customer_name}
            {customerTier && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border font-['JetBrains_Mono'] text-amber-400 bg-amber-900/20 border-amber-700/50">
                {customerTier.name} ({customerTier.markup_percent}%)
              </span>
            )}
          </p>
        </div>
        <button onClick={() => setShowInternal(!showInternal)}
          className="flex items-center gap-2 px-3 py-1.5 border border-[#2D3F5E] text-[#5C6A7A] rounded-sm text-xs hover:border-[#3B82F6]/50 hover:text-[#3B82F6] transition-colors font-['JetBrains_Mono']">
          {showInternal ? <EyeOff size={12} /> : <Eye size={12} />}
          {showInternal ? 'Hide Internal' : 'Show Internal'}
        </button>
      </div>

      <div className="grid grid-cols-12 gap-5">
        {/* Left — Pricing Config */}
        <div className="col-span-4 space-y-4">
          {/* Quick markup presets */}
          <div className="card-industrial p-5">
            <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-3">Markup %</p>
            <div className="flex gap-2 mb-3">
              {MARKUP_PRESETS.map(preset => (
                <button key={preset.value} type="button"
                  onClick={() => { setMarkupPct(preset.value); setSelectedTierId(''); }}
                  className={`flex-1 py-2.5 rounded-sm border text-sm font-bold font-['JetBrains_Mono'] transition-all ${
                    markupPct === preset.value && !isCustomMarkup
                      ? 'bg-[#3B82F6] text-white border-[#3B82F6]'
                      : 'bg-[#0A0F1A] text-[#8B9AAB] border-[#2D3F5E] hover:border-[#3B82F6]/50 hover:text-[#E8EAED]'
                  }`}>
                  {preset.label}
                </button>
              ))}
              <button type="button"
                onClick={() => setIsCustomMarkup(true)}
                className={`flex-1 py-2.5 rounded-sm border text-sm font-bold font-['JetBrains_Mono'] transition-all ${
                  isCustomMarkup
                    ? 'bg-[#3B82F6] text-white border-[#3B82F6]'
                    : 'bg-[#0A0F1A] text-[#8B9AAB] border-[#2D3F5E] hover:border-[#3B82F6]/50'
                }`}>
                Custom
              </button>
            </div>
            {isCustomMarkup && (
              <div className="flex items-center gap-2">
                <input
                  type="number" step="0.1" min="0" max="200"
                  value={markupPct}
                  onChange={e => setMarkupPct(parseFloat(e.target.value) || 0)}
                  autoFocus
                  className="w-24 h-9 px-3 bg-[#0A0F1A] border border-[#3B82F6] rounded-sm text-white font-['JetBrains_Mono'] text-sm focus:outline-none"
                  data-testid="custom-markup-input"
                />
                <span className="text-[#3B82F6] font-['JetBrains_Mono'] font-bold text-lg">%</span>
                <p className="text-xs text-[#5C6A7A] font-['IBM_Plex_Sans']">custom markup</p>
              </div>
            )}
            <p className="text-xs text-[#5C6A7A] font-['IBM_Plex_Sans'] mt-3">
              Active: <span className="text-[#3B82F6] font-['JetBrains_Mono'] font-bold">{markupPct}%</span> markup applied to all line items
            </p>
          </div>

          {/* Service Fee — manual dollar amount */}
          <div className="card-industrial p-5">
            <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-3">Service Fee</p>
            <div className="flex items-center gap-2">
              <span className="text-[#3B82F6] font-['JetBrains_Mono'] font-bold text-xl">$</span>
              <input
                type="number" step="0.01" min="0"
                value={serviceFee}
                onChange={e => setServiceFee(e.target.value)}
                placeholder="0.00"
                className="flex-1 h-11 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['JetBrains_Mono'] text-lg font-bold focus:outline-none focus:border-[#3B82F6] transition-colors"
                data-testid="service-fee-input"
              />
            </div>
            <p className="text-xs text-[#5C6A7A] font-['IBM_Plex_Sans'] mt-2">Flat service / management fee added to invoice</p>
            {/* Quick service fee shortcuts */}
            <div className="flex gap-1.5 mt-3">
              {[0, 50, 75, 100, 150, 200].map(v => (
                <button key={v} type="button"
                  onClick={() => setServiceFee(v)}
                  className={`px-2 py-1 text-[10px] font-['JetBrains_Mono'] rounded-sm border transition-all ${
                    parseFloat(serviceFee) === v ? 'bg-[#3B82F6] text-white border-[#3B82F6]' : 'border-[#2D3F5E] text-[#5C6A7A] hover:border-[#3B82F6]/40'
                  }`}>
                  ${v}
                </button>
              ))}
            </div>
          </div>

          {/* Tax */}
          <div className="card-industrial p-5">
            <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-3">Tax Rate (optional)</p>
            <div className="flex items-center gap-2">
              <input type="number" step="0.01" min="0" value={taxRate} onChange={e => { setTaxRate(e.target.value); setSelectedTierId(''); }}
                placeholder="0"
                className="w-20 h-9 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['JetBrains_Mono'] text-sm focus:outline-none focus:border-[#3B82F6]" />
              <span className="text-[#8B9AAB] font-['JetBrains_Mono']">%</span>
              <span className="text-xs text-[#5C6A7A] font-['IBM_Plex_Sans']">= {fmt(taxAmt)}</span>
            </div>
            <div className="flex gap-1.5 mt-3">
              {[0, 5, 7, 8.25, 10].map(v => (
                <button key={v} type="button"
                  onClick={() => { setTaxRate(v); setSelectedTierId(''); }}
                  className={`px-2 py-1 text-[10px] font-['JetBrains_Mono'] rounded-sm border transition-all ${
                    parseFloat(taxRate) === v ? 'bg-[#3B82F6] text-white border-[#3B82F6]' : 'border-[#2D3F5E] text-[#5C6A7A] hover:border-[#3B82F6]/40'
                  }`}
                  data-testid={`tax-shortcut-${v}`}>
                  {v}%
                </button>
              ))}
            </div>
          </div>

          {/* Internal Totals (Ops view) */}
          {showInternal && (
            <div className="card-industrial p-5 border-[#3B82F6]/20">
              <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-3">Internal View (Ops Only)</p>
              <div className="space-y-2 text-xs font-['JetBrains_Mono']">
                {[
                  ['Base Total (shop)', fmt(baseSubtotal), 'text-[#B8C0CC]'],
                  ['Markup', `+${fmt(markupAmt)}`, 'text-[#3B82F6]'],
                  ['Subtotal After Markup', fmt(subtotalAfterMarkup), 'text-white'],
                  ['Service Fee', `+${fmt(serviceFee)}`, 'text-[#3B82F6]'],
                  ['Tax', `+${fmt(taxAmt)}`, 'text-[#B8C0CC]'],
                ].map(([label, value, color]) => (
                  <div key={label} className="flex justify-between">
                    <span className="text-[#5C6A7A]">{label}</span>
                    <span className={color}>{value}</span>
                  </div>
                ))}
                <div className="border-t border-[#2D3F5E] pt-2 flex justify-between font-bold">
                  <span className="text-white">Total Due</span>
                  <span className="text-[#3B82F6] text-sm">{fmt(totalDue)}</span>
                </div>
                <div className="border-t border-[#2D3F5E]/50 pt-2 flex justify-between">
                  <span className="text-[#57EB31]">Our Profit</span>
                  <span className="text-[#57EB31] font-bold">{fmt(profitEstimate)}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right — Item Selection + Customer Preview */}
        <div className="col-span-8 space-y-4">
          {/* Item selection */}
          <div className="card-industrial overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#2D3F5E]">
              <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A]">Select Items to Include</p>
              <button type="button" onClick={addManualItem}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-['JetBrains_Mono'] font-bold uppercase tracking-wide text-[#3B82F6] border border-[#3B82F6]/40 rounded-sm hover:bg-[#3B82F6]/10 transition-colors"
                data-testid="add-item-btn">
                + Add Item
              </button>
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[#2D3F5E]">
                  <th className="px-4 py-2 w-8"></th>
                  <th className="text-left px-4 py-2 text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A]">DESCRIPTION (CUSTOMER-FACING)</th>
                  <th className="text-left px-4 py-2 text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A]">QTY</th>
                  {showInternal && <th className="text-left px-4 py-2 text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A]">BASE COST</th>}
                  {showInternal && <th className="text-left px-4 py-2 text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A]">MARKUP %</th>}
                  <th className="text-left px-4 py-2 text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A]">CUST. PRICE</th>
                </tr>
              </thead>
              <tbody>
                {selectedItems.map((item, idx) => {
                  const custItem = customerItems.find((_, ci) => ci === selectedItems.filter(i=>i.selected).indexOf(item));
                  const finalUnit = item.override_final_unit_price != null ? item.override_final_unit_price
                    : item.override_markup_percent != null ? Math.round((item.unit_cost || 0) * (1 + item.override_markup_percent/100) * 100)/100
                    : Math.round((item.unit_cost || 0) * (1 + markupPct/100) * 100)/100;
                  return (
                    <tr key={item.id || idx} className={`border-b border-[#2D3F5E]/30 ${!item.selected ? 'opacity-40' : ''} ${item.category === 'labor' ? 'bg-amber-500/5' : item.category === 'fee' ? 'bg-red-500/5' : ''}`}>
                      <td className="px-4 py-2">
                        <input type="checkbox" checked={item.selected} onChange={e => {
                          const next = [...selectedItems]; next[idx] = { ...next[idx], selected: e.target.checked }; setSelectedItems(next);
                        }} className="w-4 h-4 accent-[#3B82F6]" />
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          {item.category && item.category !== 'part' && (
                            <span className={`shrink-0 px-1.5 py-0.5 rounded text-[8px] font-['JetBrains_Mono'] font-bold uppercase ${
                              item.category === 'labor' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
                              'bg-red-400/20 text-red-400 border border-red-400/30'
                            }`}>{item.category}</span>
                          )}
                          <input value={item.desc_display || item.desc} onChange={e => {
                            const next = [...selectedItems]; next[idx] = { ...next[idx], desc_display: e.target.value }; setSelectedItems(next);
                          }} className="w-full h-7 px-2 bg-[#0A0F1A] border border-[#2D3F5E]/60 rounded-sm text-white font-['IBM_Plex_Sans'] text-xs focus:outline-none focus:border-[#3B82F6]" />
                        </div>
                      </td>
                      <td className="px-3 py-2 w-16">
                        {item.is_manual ? (
                          <input type="number" step="1" min="1" value={item.qty}
                            onChange={e => {
                              const next = [...selectedItems]; const v = parseFloat(e.target.value) || 1;
                              next[idx] = { ...next[idx], qty: v, line_cost: v * (next[idx].unit_cost || 0) }; setSelectedItems(next);
                            }}
                            className="w-14 h-7 px-2 bg-[#0A0F1A] border border-[#2D3F5E]/60 rounded-sm text-white font-['JetBrains_Mono'] text-xs focus:outline-none focus:border-[#3B82F6]" />
                        ) : (
                          <span className="font-['JetBrains_Mono'] text-[#B8C0CC]">{item.qty}</span>
                        )}
                      </td>
                      {showInternal && (
                        <td className="px-3 py-2 w-24">
                          {item.is_manual ? (
                            <input type="number" step="0.01" min="0" value={item.unit_cost}
                              onChange={e => {
                                const next = [...selectedItems]; const v = parseFloat(e.target.value) || 0;
                                next[idx] = { ...next[idx], unit_cost: v, line_cost: v * (next[idx].qty || 1) }; setSelectedItems(next);
                              }}
                              className="w-20 h-7 px-2 bg-[#0A0F1A] border border-[#2D3F5E]/60 rounded-sm text-white font-['JetBrains_Mono'] text-xs focus:outline-none focus:border-[#3B82F6]" />
                          ) : (
                            <span className="font-['JetBrains_Mono'] text-[#5C6A7A]">{fmt(item.unit_cost)}</span>
                          )}
                        </td>
                      )}
                      {showInternal && (
                        <td className="px-3 py-2 w-24">
                          <div className="flex items-center gap-1">
                            <input
                              type="number" step="0.1" min="0"
                              value={item.override_markup_percent != null ? item.override_markup_percent : markupPct}
                              onChange={e => {
                                const val = e.target.value === '' ? null : parseFloat(e.target.value);
                                const next = [...selectedItems];
                                next[idx] = { ...next[idx], override_markup_percent: val === markupPct ? null : val, override_final_unit_price: null };
                                setSelectedItems(next);
                              }}
                              className={`w-16 h-7 px-2 bg-[#0A0F1A] border rounded-sm font-['JetBrains_Mono'] text-xs focus:outline-none focus:border-[#3B82F6] ${
                                item.override_markup_percent != null ? 'border-[#3B82F6] text-[#3B82F6]' : 'border-[#2D3F5E]/60 text-[#5C6A7A]'
                              }`}
                            />
                            <span className="text-[#5C6A7A] text-[10px]">%</span>
                          </div>
                        </td>
                      )}
                      <td className="px-3 py-2 w-28">
                        <input
                          type="number" step="0.01" min="0"
                          value={item.override_final_unit_price != null ? item.override_final_unit_price : finalUnit}
                          onChange={e => {
                            const val = e.target.value === '' ? null : parseFloat(e.target.value);
                            const next = [...selectedItems];
                            next[idx] = { ...next[idx], override_final_unit_price: val, override_markup_percent: null };
                            setSelectedItems(next);
                          }}
                          className={`w-24 h-7 px-2 bg-[#0A0F1A] border rounded-sm font-['JetBrains_Mono'] text-xs font-bold focus:outline-none focus:border-[#57EB31] ${
                            item.override_final_unit_price != null ? 'border-[#57EB31] text-[#57EB31]' : 'border-[#2D3F5E]/60 text-[#57EB31]'
                          }`}
                        />
                      </td>
                      {item.is_manual && (
                        <td className="px-1 py-2 w-8">
                          <button type="button" onClick={() => setSelectedItems(prev => prev.filter((_, i) => i !== idx))}
                            className="text-[#5C6A7A] hover:text-red-400 transition-colors text-xs" data-testid={`remove-item-${idx}`}>
                            ✕
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Customer-facing preview */}
          <div className="card-industrial p-5">
            <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widests text-[#5C6A7A] mb-3">Customer Invoice Preview</p>
            <div className="bg-white rounded-sm p-5 text-white">
              <div className="flex justify-between items-start mb-4 pb-3 border-b-2 border-[#3B82F6]">
                <div>
                  <p className="font-bold text-lg" style={{fontFamily:'Helvetica,Arial,sans-serif'}}>Millennials Maintenance</p>
                  <p className="text-xs text-gray-500">{si?.customer_name}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-800">INVOICE</p>
                  <p className="text-xs text-gray-500">Preview</p>
                </div>
              </div>
              <table className="w-full text-sm mb-4">
                <thead>
                  <tr className="bg-[#0F172A] text-white">
                    {['Description','Qty','Unit Price','Total'].map(h => (
                      <th key={h} className="text-left px-3 py-2 text-xs font-bold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {customerItems.map((item, i) => (
                    <tr key={i} className={i%2===0?'bg-gray-50':''}>
                      <td className="px-3 py-2 text-xs">{item.desc}</td>
                      <td className="px-3 py-2 text-xs text-center">{item.qty}</td>
                      <td className="px-3 py-2 text-xs text-right">{fmt(item.unit_price)}</td>
                      <td className="px-3 py-2 text-xs text-right font-bold">{fmt(item.line_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex justify-end">
                <div className="w-48 space-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>{fmt(subtotalAfterMarkup)}</span></div>
                  {parseFloat(serviceFee) > 0 && <div className="flex justify-between"><span className="text-gray-500">Service Fee</span><span>{fmt(serviceFee)}</span></div>}
                  {taxAmt > 0 && <div className="flex justify-between"><span className="text-gray-500">Tax</span><span>{fmt(taxAmt)}</span></div>}
                  <div className="flex justify-between font-bold border-t border-gray-300 pt-1 text-sm">
                    <span>TOTAL DUE</span><span className="text-white">{fmt(totalDue)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button onClick={handleContinue} disabled={creating || activeItems.length === 0} data-testid="continue-to-finalize-btn"
              className="flex items-center gap-2 px-6 py-3 bg-[#3B82F6] text-white rounded-sm font-['Manrope'] font-bold text-sm uppercase tracking-wide hover:bg-[#2563EB] disabled:opacity-50 shadow-[0_0_20px_rgba(59,130,246,0.2)]">
              {creating ? <Loader2 size={15} className="animate-spin" /> : null} Continue to Final Invoice <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
